// Copyright (c) Microsoft Corporation. All rights reserved.

import { error } from 'console';
import Settings from '../components/Settings/Settings';
import { ITokenService } from '../tokenService';
import {
    FilterSearchResponse,
    ProductSearchResponse,
    SemanticSearchResponse,
    TitleSearchResponse,
} from '../types/ArtifactTypes';
import {
    CopySessionResponse,
    FindSessionResponse,
    SessionsResponse,
} from '../types/SessionTypes';
import { RootState, addPseudoMessage, store } from '../utils/store';
import {
    getExportErrorResponse,
    parseExportResponseBody,
} from '../utils/exportResponse';

/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */

export interface UserSegmentResult {
    script: string;
    resultsKey: string;
    users?: number;
    exception?: string;
    sampleUsers: string[];
}

export interface ChatInteraction {
    type?: 'message' | 'code' | 'error';
    input: string;
    responseMessage?: string;
    pseudocode: string;
    luaScript?: string;
    exception?: string;
    result?: UserSegmentResult; // replace with the type of your result
}

export interface ChatItem {
    message: string;
    role: string;
}

// Type: Microsoft.CampaignGPT.Orchestration.Common.Contracts.UserSegmentRequest, Common

export interface ChatSegmentRequest {
    role: 'User';
    userInput?: string;
    courseCorrection?: string;
    script?: string;
    segment?: string;
    endpoint: 'chat';
    sessionId: string;
    sessionName?: string;
    dimensionContext?: string;
}

// Type: Microsoft.CampaignGPT.Orchestration.Common.Contracts.UserSegmentResponse, Common

export interface ChatSegmentResponse {
    role: 'Assistant';
    script?: string;
    confidence?: string;
    segment?: string;
    responseText: string;
    endpoint: string;
    unparsedGptResponse: string;
    gptResponseParsingException?: string;
    sessionId: string;
    exception?: string;
    executeTimeMs?: number;
    users?: number;
    script_error?: string;
    script_stack?: string;
    sessionName?: string;
    missingTitleContext?: string[];
    missingProductContext?: string[];
    status?: number;
    idMapping?: Record<string, string>;
    validation?: AnalyticsValidation;
}

// Verdict from the analytics results validator: does the result plausibly answer the question?
export interface AnalyticsValidation {
    resultsLookValid: boolean;
    reasoning?: string;
}

export interface ChatMessage {
    message: string;
    role: number;
    luaScript?: string;
    result?: UserSegmentResult;
}

export interface ChatBundle {
    request?: ChatSegmentRequest;
    response?: ChatSegmentResponse;
    script?: ChatScriptResponse;
    reasoning?: ReasoningMessage;
    totalMessages?: number;
}

// Type: Microsoft.CampaignGPT.Orchestration.Common.Contracts.ScriptExecuteRequest, Common

export interface ChatScriptRequest {
    role: 'User';
    script: string;
    endpoint: 'executeScript';
    sessionName?: string;
}

export interface HardCodedMessage {
    role: 'System';
    sessionId: string;
    response: string;
    message: string;

}
export interface NotificationBanner {
    // Unique, versioned dismissal key (e.g. "maintenance-window-v1"). Controls dismissal caching.
    id: string;
    // Notification body. Supports basic HTML (<b>, <a>, <br/>, etc.).
    message: string;
    // Where the notification appears: sticky banner vs. inline status bar.
    type: 'banner' | 'statusBar';
    // How the notification looks: info (blue) vs. warning (orange/red).
    severity: 'info' | 'warning';
    // Whether the notification is active.
    showMessage: boolean;
    // Optional targeting field returned by the server.
    email?: string;
}
export interface SystemMessage {
    role: 'System';
    sessionId: string;
    message: string;
    endpoint: string;
    sessionName?: string;
}
export interface ReasoningMessage {
    role: 'AssistantReasoning';
    sessionId: string;
    message: string[];
    endpoint: string;
    sessionName?: string;
}


export interface ChatError {
    role: 'Error';
    sessionId: string;
    message: string;
    endpoint: '';
    sessionName?: string;
    length: number;
    status: number;
    segment?:null;
    response?: string,
    Pseudocode?: string,
    LuaScript?: string,
    executionTimeMs?: number,
    users?:number,
    exception?: string,
    scriptError?: string,

}

// Type: Microsoft.CampaignGPT.Orchestration.Common.Contracts.ScriptExecuteResponse, Common

export interface ChatScriptResponse {
    role: 'Tool';
    script?: string;
    endpoint: string;
    response: string;
    script_error?: string;
    script_stack?: string;
    users: number;
    executeTimeMs: number;
    countAdultAccounts: number;
    countFirstPartyMarketingOptIn: number;
    countThirdPartyMarketingOptIn: number;
    countPersonalizedPurchaseUpsellOptIn: number;
    sessionName?: string;
    status?: number;
}

// Raw result of re-executing an analytics (analyzeUser/emit) script without GPT interpretation.
// `results` is a flat dict of emitted label → count; `executeTimeMs` is pulled out of the same dict.
export interface AnalyticsRawResult {
    status: number;
    results: Record<string, number | string>;
    executeTimeMs?: number;
    error?: string;
}

export type ChatElement =
    | ChatSegmentRequest
    | ChatSegmentResponse
    | ChatScriptRequest
    | ChatScriptResponse
    | ChatError
    | SystemMessage
    | ReasoningMessage;

export interface MappedTitle {
    key: string;
    name: string;
    titleId: string;
}

export interface ExportSegmentRequest {
    id?:string;
    partitionKey?:string;
    sessionId?:string;
    dataset?: string;
    exportType: string;
    datasetName: string;
    description: string;
    userEmail?: string;
    messageIndex: number;
    timestamp?: string;
    chatUser: ChatSegmentRequest;
    chatInteraction: ChatSegmentResponse;
    chatScript?: ChatScriptResponse | null;
    isAutoRefresh?: boolean;
    shellId?: string;
}

export interface ExportSegmentResponse {
    ExecuteTimeMs: number;
    Count: number;
    Error?: string;
    Details?: string;
}

export interface ContextItem {
    FieldName: string;
    Value: string;
    ContextType: string;
    DisplayName: string;
    addedAt?: number;
}

export interface ProductCatalog {
    Products: {
        LocalizedProperties: {
            Images: { ImagePurpose: string; Uri: string }[];
        }[];
    }[];
}

export interface samplePrompt {
    title: string;
    description: string;
    icon: string;
    disabled?: boolean;
}

export interface sampleQueries {
    samplePrompts: samplePrompt[];
    isActivision: boolean;
}

// Field Values Autocomplete types

export interface FieldValueFieldEntry {
    schema: string;
    field: string;
    count: number;
}

export interface FieldValueSearchEntry {
    schema: string;
    field: string;
    fieldMatch: boolean;
    values: string[];
}

export interface FieldValueDetailEntry {
    schema: string;
    field: string;
    values: string[];
}

export type FieldValuesSearchResult =
    | FieldValueFieldEntry[]
    | FieldValueSearchEntry[]
    | FieldValueDetailEntry;

export async function fetchProductLogo(productId: string): Promise<string> {
    const response = await fetch(
        `https://displaycatalog.mp.microsoft.com/v7.0/products?market=US&languages=en-US&bigIds=${productId}`
    );
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: ProductCatalog = await response.json();
    var artJSON =
        data.Products[0].LocalizedProperties[0].Images.find(
            (obj) => obj.ImagePurpose === 'Icon'
        ) ??
        data.Products[0].LocalizedProperties[0].Images.find(
            (obj) => obj.ImagePurpose === 'BoxArt'
        ) ??
        data.Products[0].LocalizedProperties[0].Images.find(
            (obj) => obj.ImagePurpose === 'Logo'
        );
    return artJSON?.Uri ?? 'none';
}

const tokenScope: string =
    'api://7c720fd5-1d2c-4726-83ce-3a3b4b01d96e/access_as_user';

export class CopilotService {
    private static _instance: CopilotService;
    private static currentIntent: string;
    private static currentSession: string;
    private tokenService: ITokenService;

    private readonly trellisChatEndpoint =
        'https://wa-orchestration-staging.azurewebsites.net';

    private constructor(tokenService: ITokenService) {
        this.tokenService = tokenService;
    }

    public getSettings() {
        const state: RootState = store.getState();
        return state.settings;
    }

    public static getInstance(tokenService: ITokenService): CopilotService {
        if (!CopilotService._instance) {
            CopilotService._instance = new CopilotService(tokenService);
        }

        return CopilotService._instance;
    }

    public static setIntent(intent: string) {
        CopilotService.currentIntent = intent;
    }

    public static getIntent() {
        return CopilotService.currentIntent;
    }

    public async SegmentCombinedAPI(
        userInput: string,
        endpoint: string,
        chat: ChatItem[],
        sessionId?: string,
        segment?: string,
        script?: string,
        dataset?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const settings = this.getSettings();

        //const url = `${apiEndpont}/UserSegment/combined?store=dxu&runScript=true`;
        let chatPath = 'chat';

        if (settings.useChatv2) {
            chatPath = 'chatv2';
        }
        const url = `${apiEndpont}/UserSegment/${chatPath}?runScript=false&sessionId=${sessionId}${dataset ? `&dataset=${dataset}` : ''}`;

        const contextArray: ContextItem[] = Object.keys(settings.context).map(
            (key) => settings.context[key]
        );

        const postBody = {
            userInput: userInput,
            role: 'User',
            dimensionContext: JSON.stringify(contextArray),
            enabledEvaluation: settings.enableEvaluation,
            //chat: chat,
            //script: script,
            //segment: segment,
        };

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: ChatSegmentResponse = await response.json();

            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error: unknown) {
            // eslint-disable-next-line no-console

            // Initialize a default error message
            let errorMessage: string;

            // Narrow down the type of 'error' using type guards
            if (error instanceof Error) {
                // Now we're sure it's an Error object and has the 'message' property
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                // The error is a string, so we can use it directly
                errorMessage = error;
            } else {
                // If it's not an Error object or a string, we assign a default message
                errorMessage = 'An unexpected error occurred';
            }
            console.log(error);
            const fakeResponse: ChatSegmentResponse = {
                role: 'Assistant',
                sessionId: sessionId ?? '',
                endpoint: 'chat',
                status: 500,
                responseText: errorMessage,
                unparsedGptResponse: 'Server Issue',
                exception: 'Server Issue'
            };

            return fakeResponse;
        }
    }

    public async AnalyticsAPI(
        userInput: string,
        endpoint: string,
        dataset?: string,
        chatHistory?: { role: string; content: string }[],
        dimensionContext?: string,
        sessionId?: string,
        onPhase1?: (description: string, script: string) => void
    ): Promise<ChatSegmentResponse> {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpoint = endpoint || this.trellisChatEndpoint;
        if (apiEndpoint.endsWith('/')) {
            apiEndpoint = apiEndpoint.slice(0, -1);
        }

        const sessionParam = sessionId ? `&sessionId=${sessionId}` : '';
        const url = `${apiEndpoint}/UserSegment/analytics?dataset=${dataset || ''}${sessionParam}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            // Phase 1: Generate script only
            const phase1Body = { userInput, chatHistory, dimensionContext, generateOnly: true };
            const phase1Response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(phase1Body),
                headers: headers,
            });
            const phase1 = await phase1Response.json();

            if (phase1.clarification) {
                return {
                    role: 'Assistant',
                    sessionId: phase1.sessionId || '',
                    endpoint: 'analytics',
                    status: phase1Response.status,
                    responseText: phase1.response || phase1.clarification,
                    unparsedGptResponse: '',
                };
            }

            // Conversational response — GPT answered without needing a script
            if (!phase1.script && phase1.interpretation) {
                return {
                    role: 'Assistant',
                    sessionId: phase1.sessionId || '',
                    endpoint: 'analytics',
                    status: 200,
                    responseText: phase1.interpretation,
                    unparsedGptResponse: '',
                };
            }

            if (!phase1.script) {
                return {
                    role: 'Assistant',
                    sessionId: phase1.sessionId || '',
                    endpoint: 'analytics',
                    status: 502,
                    responseText: phase1.error || 'Failed to generate analytics script',
                    unparsedGptResponse: JSON.stringify(phase1),
                };
            }

            // Notify UI of Phase 1 completion
            if (onPhase1) {
                onPhase1(phase1.scriptDescription || 'Analyzing...', phase1.script);
            }

            // Phase 2: Execute script + interpret results
            const phase2SessionParam = phase1.sessionId ? `&sessionId=${phase1.sessionId}` : sessionParam;
            const phase2Url = `${apiEndpoint}/UserSegment/analytics?dataset=${dataset || ''}${phase2SessionParam}`;
            const phase2Body = {
                userInput,
                script: phase1.script,
                scriptDescription: phase1.scriptDescription,
                topN: phase1.topN,
                chatHistory,
                dimensionContext,
            };
            const phase2Response = await fetch(phase2Url, {
                method: 'POST',
                body: JSON.stringify(phase2Body),
                headers: headers,
            });
            const responseBody = await phase2Response.json();

            // Map analytics response to ChatSegmentResponse shape
            const chatResponse: ChatSegmentResponse = {
                role: 'Assistant',
                sessionId: responseBody.sessionId || phase1.sessionId || '',
                endpoint: 'analytics',
                status: phase2Response.status,
                responseText: responseBody.interpretation || responseBody.error || 'No results',
                unparsedGptResponse: JSON.stringify(responseBody.rawResults || responseBody),
                script: phase1.script,
                executeTimeMs: responseBody.rawResults ? JSON.parse(responseBody.rawResults)?.executeTimeMs : undefined,
                idMapping: responseBody.idMapping || undefined,
                validation: responseBody.validation || undefined,
            };

            return chatResponse;
        } catch (error: unknown) {
            let errorMessage: string;
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else {
                errorMessage = 'An unexpected error occurred';
            }
            console.log(error);
            return {
                role: 'Assistant',
                sessionId: '',
                endpoint: 'analytics',
                status: 500,
                responseText: errorMessage,
                unparsedGptResponse: 'Server Issue',
                exception: 'Server Issue'
            };
        }
    }

    public async RetryScriptAPI(
        script: string,
        error: string,
        endpoint: string,
        sessionId?: string,
        segment?: string,
        dataset?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }
        const settings = this.getSettings();
        let chatPath = 'chat';

        if (settings.useChatv2) {
            chatPath = 'chatv2';
        }
        const url = `${apiEndpont}/UserSegment/${chatPath}?runScript=false&sessionId=${sessionId}${dataset ? `&dataset=${dataset}` : ''}`;

        const postBody = {
            courseCorrection: `The error: '${error}' was generated and I ran the following script you produced, please return me a new script with the error corrected: ${script}`,
            role: 'System',
        };

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(postBody),
                headers: headers,
            });

            const settings = this.getSettings();

            store.dispatch(
                addPseudoMessage({
                    courseCorrection: `The error: '${error}' was generated and I ran the following script you produced, please return me a new script with the error corrected: ${script}`,
                    sessionId: settings.sessionId,
                    role: 'User',
                    endpoint: 'chat',
                })
            );
            const responseBody: ChatSegmentResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error: unknown) {
            // eslint-disable-next-line no-console

            // Initialize a default error message
            let errorMessage: string;

            // Narrow down the type of 'error' using type guards
            if (error instanceof Error) {
                // Now we're sure it's an Error object and has the 'message' property
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                // The error is a string, so we can use it directly
                errorMessage = error;
            } else {
                // If it's not an Error object or a string, we assign a default message
                errorMessage = 'An unexpected error occurred with the retry of the script. Please click on "New Chat" and try again';
            }
            console.log(error);
            const fakeResponse: ChatSegmentResponse = {
                role: 'Assistant',
                sessionId: sessionId ?? '',
                endpoint: 'chat',
                status: 500,
                responseText: errorMessage,
                unparsedGptResponse: 'Server Issue',
                exception: 'Server Issue'
            };

            return fakeResponse;
        }
    }

    // User Segment

    public async trellisUserSegmentScriptAPI(
        script: string,
        sessionId: string,
        titlesMapped?: MappedTitle[],
        endpoint?: string,
        dataset?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const postBody = {
            script: script,
            titlesMapped: titlesMapped,
        };

        const url = `${apiEndpont}/UserSegment/execute/v2?store=dxu&sessionId=${sessionId}${dataset ? `&dataset=${dataset}` : ''}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: ChatScriptResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            const fakeResponse: ChatScriptResponse = {
                role: 'Tool',
                script: '',
                endpoint: 'chat',
                response: '',
                script_error:  'An error has ocurred -'+ error,
                users: 0,
                executeTimeMs: 0,
                countFirstPartyMarketingOptIn: 0,
                countThirdPartyMarketingOptIn: 0,
                countPersonalizedPurchaseUpsellOptIn: 0,
                countAdultAccounts: 0,
                status: 500
            };

            return fakeResponse;
        }
    }

    public async executeAdhocScriptAPI(
        script: string,
        titlesMapped?: MappedTitle[],
        endpoint?: string,
        dataset?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const postBody = {
            script: script,
            titlesMapped: titlesMapped,
        };

        const url = `${apiEndpont}/UserSegment/execute/adhoc?store=dxu${dataset ? `&dataset=${dataset}` : ''}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: ChatScriptResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: ChatScriptResponse = {
                role: 'Tool',
                script: '',
                endpoint: 'chat',
                response: '',
                script_error: 'An error has occurred with the script - ' + error,
                users: 0,
                executeTimeMs: 0,
                countFirstPartyMarketingOptIn: 0,
                countThirdPartyMarketingOptIn: 0,
                countPersonalizedPurchaseUpsellOptIn: 0,
                countAdultAccounts: 0,
                status: 500
            };

            return fakeResponse;
        }
    }

    /**
     * Re-executes an analytics (analyzeUser/emit) script directly, without GPT
     * generation or interpretation. Posts the script to the analytics endpoint
     * with no userInput, so the backend skips both prompt phases and returns the
     * raw emitted label → count dict. Used by the "Rerun Script" button on
     * analytics messages, where the segmentation execute/adhoc path does not apply.
     */
    public async executeAnalyticsScriptAPI(
        script: string,
        endpoint?: string,
        dataset?: string
    ): Promise<AnalyticsRawResult> {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/UserSegment/analytics?dataset=${dataset || ''}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            // Direct execution: script only, no userInput → raw results, no interpretation.
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ script }),
                headers: headers,
            });

            const responseText = await response.text();
            let raw: unknown;
            if (responseText) {
                try {
                    raw = JSON.parse(responseText);
                } catch {
                    raw = responseText;
                }
            }
            const rawObject = raw && typeof raw === 'object' && !Array.isArray(raw)
                ? raw as Record<string, unknown>
                : undefined;

            if (!response.ok) {
                const responseError = rawObject && typeof rawObject.error === 'string'
                    ? rawObject.error
                    : typeof raw === 'string' && raw.trim()
                        ? raw
                        : 'Failed to execute analytics script';
                return {
                    status: response.status,
                    results: {},
                    error: responseError,
                };
            }
            if (!rawObject) {
                return {
                    status: 500,
                    results: {},
                    error: 'Analytics service returned an invalid response',
                };
            }

            // Backend returns a flat dict of emitted label → count plus meta keys
            // (executeTimeMs, resultsKey). Split meta out from the emitted data.
            const results: Record<string, number | string> = {};
            let executeTimeMs: number | undefined;
            const metaKeys = new Set(['executeTimeMs', 'resultsKey']);
            if (rawObject) {
                for (const [key, value] of Object.entries(rawObject)) {
                    if (key === 'executeTimeMs' && typeof value === 'number') {
                        executeTimeMs = value;
                    }
                    if (!metaKeys.has(key)) {
                        results[key] = value as number | string;
                    }
                }
            }

            return {
                status: response.status,
                results,
                executeTimeMs,
            };
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            return {
                status: 500,
                results: {},
                error: 'An error has occurred with the script - ' + error,
            };
        }
    }

    public async titleResolveAPI(title: string, count: number, type: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        const settings = this.getSettings();
        let apiEndpont = settings.apiEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Artifacts/resolvetitles?prefix=${title}&maxItems=${count}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                //body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: SemanticSearchResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: SemanticSearchResponse = {
                titlesMapped: [],
                status: 500
            };

            return fakeResponse;
        }
    }

    public async searchAPI(title: string, count: number, type: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        const settings = this.getSettings();
        let apiEndpont = settings.apiEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Artifacts/${type}?prefix=${title}&maxItems=${count}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                //body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: TitleSearchResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: TitleSearchResponse = {
                results: [],
                status: 500
            };

            return fakeResponse;
        }
    }

    public async filterSearchAPI(title: string, count: number, type: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        const settings = this.getSettings();
        let apiEndpont = settings.apiEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Artifacts/${type}?prefix=${title}&maxItems=${count}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                //body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: FilterSearchResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

             const fakeResponse: FilterSearchResponse = {
                results: [],
                status: 500
            };

            return fakeResponse;
        }
    }

    public async productSearchAPI(title: string, count: number, type: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        const settings = this.getSettings();
        let apiEndpont = settings.apiEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Artifacts/${type}?prefix=${title}&maxItems=${count}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                //body: JSON.stringify(postBody),
                headers: headers,
            });
            const responseBody: ProductSearchResponse = await response.json();
            if(!responseBody.status && response.status) {
                responseBody.status = response.status
            }
            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: ProductSearchResponse = {
                results: [],
                status: 500
            };

            return fakeResponse;
        }
    }

    public async findSession(sessionId: string, endpoint?: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Sessions/find/${sessionId}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            const responseBody: FindSessionResponse = await response.json();

            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: FindSessionResponse = [];

            return fakeResponse;
        }
    }

    public async getSession(
        user: string,
        sessionId: string,
        endpoint?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Sessions/${sessionId}?user=${user}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            const responseBody: any = await response.json();

            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: ChatSegmentResponse = {
                role: 'Assistant',
                sessionId: sessionId ?? '',
                responseText: 'An error has occurred while fetching the session - '+ error,
                endpoint: 'chat',                
                status: 500,
                unparsedGptResponse: 'Server Issue',
                exception: 'Server Issue'
            };

            return fakeResponse;
        }
    }

    public async copySession(
        username: string,
        owner: string,
        sessionId: string,
        endpoint?: string
    ) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Sessions/copy?shareWith=${username}&sourceUser=${owner}&sourceSessionId=${sessionId}`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                //body: `"${script}"`,
                headers: headers,
            });
            const responseBody: CopySessionResponse = await response.text();

            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: CopySessionResponse = '';

            return fakeResponse;
        }
    }

    public async getUserSessions(endpoint?: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }

        const url = `${apiEndpont}/Sessions/v2?numSessions=50`;

        // Include the token in the Authorization header if it's available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            const responseBody: SessionsResponse = await response.json();

            return responseBody;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);

            const fakeResponse: SessionsResponse = {
                sessions: []
            };

            return fakeResponse;
        }
    }

    /**
     * Exports XUIDs to Azure Storage via Memory and writes the export manifest to Cosmos DB.
     * Returns structured results with error details.
     */
    public async sendExportSegmentRequest(requestBody: ExportSegmentRequest, sessionId: string, endpoint: string): Promise<ExportSegmentResponse> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);

        const export_url = `${apiEndpoint}/ExportSegment/`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        requestBody.partitionKey= " ";
        requestBody.sessionId = sessionId;
        requestBody.timestamp = Date.now().toString();

        try {
            console.log("requestBody", requestBody);
            const response = await fetch(export_url, {
                method: "POST",
                headers: headers,
                body: JSON.stringify(requestBody)
            });

            const responseText = await response.text();
            const responseBody = parseExportResponseBody(
                responseText,
                response.headers.get('content-type')
            );
            console.log('Response body:', responseBody);

            if (!response.ok) {
                console.error('Server responded with an error:', responseBody);
                return getExportErrorResponse(
                    responseBody,
                    response.status,
                    response.statusText
                );
            }

            if (typeof responseBody === 'object' && responseBody) {
                return {
                    ExecuteTimeMs: responseBody.ExecuteTimeMs ?? 0,
                    Count: responseBody.Count ?? 0,
                    Error: responseBody.Error,
                    Details: responseBody.Details,
                };
            }

            return {
                ExecuteTimeMs: 0,
                Count: 0,
                Error: 'The export service returned an invalid response.',
            };
        }
        catch (error) {
            console.error('Error submitting export request:', error);
            return { ExecuteTimeMs: 0, Count: 0, Error: 'Unable to connect to the export service. Please try again.' };
        }
    }


    public async GetLatestMessage(endpoint?: string){
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }
        const url = `${apiEndpont}/BannerMessage/GetMessage`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            const responseBody: NotificationBanner = await response.json();
            return responseBody;
        }
        catch (error) {
        console.log(error);
        }
    }
    
    /**
     * Gets the sample prompts from the CampaignGPT.Orc service for welcome screen and example
     * prompt pop-up window. If the user is an Activision user, they will be shown a different
     * set of prompts.
     * @param {string?} endpoint 
     * @returns {sampleQueries} - The sample prompts and whether the user is an Activision user
     */
    public async GetSamplePrompts(endpoint?: string, dataset?: string){
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }

        let apiEndpont = endpoint || this.trellisChatEndpoint;

        if (apiEndpont.endsWith('/')) {
            apiEndpont = apiEndpont.slice(0, -1);
        }
        const url = `${apiEndpont}/SamplePrompts${dataset ? `?dataset=${dataset}` : ''}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            return await response.json();
        }
        catch (error) {
            console.log(error);
        }
    }

    // == Debug API Methods ==
    /**
     * Analyzes a Lua script to extract field information.
     * This is for development/debugging purposes only and requires special access.
     *
     * @param script - The Lua script to analyze
     * @param withHelpers - Whether to include helper functions in analysis
     * @param sessionId - Optional session ID
     * @param endpoint - Optional API endpoint override
     * @returns Promise<AnalyzeScriptResponse> - The analysis result
     */
    public async analyzeScript(
        script: string,
        withHelpers: boolean = false,
        sessionId?: string,
        endpoint?: string,
        dataset?: string,
        mode: string = "segment"
    ): Promise<import('../types/DebugTypes').AnalyzeScriptResponse> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        const queryParts: string[] = [];
        if (sessionId) queryParts.push(`sessionId=${sessionId}`);
        if (dataset) queryParts.push(`dataset=${dataset}`);
        const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
        const url = `${apiEndpoint}/UserSegment/analyze${queryString}`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ script, withHelpers, mode })
            });

            const responseBody: import('../types/DebugTypes').AnalyzeScriptResponse = await response.json();
            
            if (!responseBody.status && response.status) {
                responseBody.status = response.status;
            }

            // 422 (Unprocessable Content) is a valid response with errors
            // Both 200 (OK) with fields and 422 with errors are valid responses
            return responseBody;
        } catch (error) {
            console.error('Script analysis error:', error);
            
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred analyzing the script';

            return {
                errors: [{
                    Type: 'NetworkError',
                    Message: errorMessage,
                    Resolution: 'Check your network connection and try again'
                }],
                status: 500
            };
        }
    }

    /**
     * Gets qualified sample users by executing a Lua qualification script.
     * This is for development/debugging purposes only and requires special access.
     *
     * @param luaScript - The Lua qualification script to execute
     * @param maxResults - Maximum number of users to return (1-100, default 10)
     * @param scrollSize - Number of users to process per batch (default 10000)
     * @param includeFields - Optional array of fields to include in response
     * @param includeNestedData - Whether to include nested data structures
     * @param maxNestedEntries - Maximum nested entries to include
     * @param queryParams - Optional query parameters (publisherRole, dateOverride)
     * @param sessionId - Optional session ID
     * @param endpoint - Optional API endpoint override
     * @returns Promise<QualifiedSampleUsersResponse> - The execution result with qualified users
     */
    public async getQualifiedSampleUsers(
        luaScript: string,
        maxResults: number = 10,
        scrollSize: number = 10000,
        includeFields?: string[],
        includeNestedData: boolean = true,
        maxNestedEntries: number = 100,
        queryParams?: import('../types/DebugTypes').QualifiedSampleUsersQueryParams,
        sessionId?: string,
        endpoint?: string,
        dataset?: string
    ): Promise<import('../types/DebugTypes').QualifiedSampleUsersResponse> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        // Build query string
        const queryParts: string[] = [];
        if (sessionId) queryParts.push(`sessionId=${sessionId}`);
        if (dataset) queryParts.push(`dataset=${dataset}`);
        if (queryParams?.publisherRole) queryParts.push(`publisherRole=${queryParams.publisherRole}`);
        if (queryParams?.dateOverride) queryParts.push(`dateOverride=${encodeURIComponent(queryParams.dateOverride)}`);
        
        const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
        const url = `${apiEndpoint}/UserDocument/qualified-sample${queryString}`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const requestBody: import('../types/DebugTypes').QualifiedSampleUsersRequest = {
            luaScript,
            maxResults,
            scrollSize,
            includeFields,
            includeNestedData,
            maxNestedEntries
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(requestBody)
            });

            const responseBody: import('../types/DebugTypes').QualifiedSampleUsersResponse = await response.json();
            
            if (!responseBody.status && response.status) {
                responseBody.status = response.status;
            }

            return responseBody;
        } catch (error) {
            console.error('Qualified sample users execution error:', error);
            
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred executing the qualification script';

            return {
                error: errorMessage,
                status: 500
            };
        }
    }

    /**
     * Executes the debug string tool endpoint.
     * This is for development/debugging purposes only and requires special access.
     *
     * @param input - The string input to send to the debug endpoint
     * @param endpoint - Optional API endpoint override
     * @returns Promise<DebugStringResponse> - The execution result with formatted response
     */
    public async executeDebugStringTool(
        input: string,
        endpoint?: string
    ): Promise<import('../types/DebugTypes').DebugStringResponse> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        const url = `${apiEndpoint}/Debug/StringToJson`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ input })
            });

            const responseBody: import('../types/DebugTypes').DebugStringResponse = await response.json();
            
            if (!responseBody.status && response.status) {
                responseBody.status = response.status;
            }

            return responseBody;
        } catch (error) {
            console.error('Debug string tool error:', error);
            
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred executing the debug string tool';

            return {
                success: false,
                error: errorMessage,
                status: 500
            };
        }
    }

    /**
     * Checks if the current user has debug access.
     * This will be used to show/hide debug UI elements.
     *
     * @param endpoint - Optional API endpoint override
     * @returns Promise<boolean> - True if user has debug access, false otherwise
     */
    public async checkDebugAccess(endpoint?: string): Promise<boolean> {
        try {
            const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
            
            const url = `${apiEndpoint}/Debug/CheckAccess`;
            
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            return response.ok;
        } catch (error) {
            console.error('Debug access check error:', error);
            return false;
        }
    }

    /**
     * Gets the combined schemas from user documents.
     * This is for development/debugging purposes only and requires special access.
     *
     * @param endpoint - Optional API endpoint override
     * @returns Promise<any> - The combined schemas response
     */
    public async getCombinedSchemas(
        endpoint?: string,
        dataset?: string
    ): Promise<any> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        const url = `${apiEndpoint}/UserDocument/schemas/combined${dataset ? `?dataset=${dataset}` : ''}`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            const responseBody: any = await response.json();
            
            return responseBody;
        } catch (error) {
            console.error('Combined schemas fetch error:', error);
            
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred fetching combined schemas';

            return {
                error: errorMessage
            };
        }
    }

    /**
     * Gets the latest dates from user documents.
     * This is for development/debugging purposes only and requires special access.
     *
     * @param endpoint - Optional API endpoint override
     * @returns Promise<any> - The latest dates response
     */
    public async getLatestDates(
        endpoint?: string,
        dataset?: string
    ): Promise<any> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        const url = `${apiEndpoint}/UserDocument/latest-dates${dataset ? `?dataset=${dataset}` : ''}`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            const responseBody: any = await response.json();
            
            return responseBody;
        } catch (error) {
            console.error('Latest dates fetch error:', error);
            
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred fetching latest dates';

            return {
                error: errorMessage
            };
        }
    }

    /**
     * Fetches the current user's profile including capabilities.
     * This determines what features the user has access to in the application.
     *
     * @param endpoint - Optional API endpoint override
     * @returns Promise<UserProfileResponse | null> - User profile with capabilities or null on error
     */
    public async getUserProfile(
        endpoint?: string
    ): Promise<import('../types/types').UserProfileResponse | null> {
        try {
            const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
            
            const url = `${apiEndpoint}/UserProfile/me`;
            
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                console.error(`Failed to fetch user profile: ${response.status} ${response.statusText}`);
                return null;
            }

            const profileData: import('../types/types').UserProfileResponse = await response.json();
            return profileData;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }
    
    /**
     * Gets the list of available datasets for the dataset picker.
     *
     * @param endpoint - Optional API endpoint override
     * @returns Promise<DatasetInfo[]> - Array of available datasets
     */
    public async getDatasets(
        endpoint?: string
    ): Promise<import('../types/types').DatasetInfo[]> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);
        
        const url = `${apiEndpoint}/UserSegment/datasets`;
        
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            const responseBody: import('../types/types').DatasetInfo[] = await response.json();
            return responseBody;
        } catch (error) {
            console.error('Datasets fetch error:', error);
            return [];
        }
    }

    // == Private Helper Functions ==
    /**
     * Searches field names and values for autocomplete.
     * Supports 4 modes:
     *   - No params: list all fields
     *   - q=<text>: search across fields and values
     *   - field=<name>: list all values for a field
     *   - field=<name>&q=<text>: filter values within a field
     */
    public async searchFieldValues(
        params?: { q?: string; field?: string },
        endpoint?: string,
        dataset?: string
    ): Promise<FieldValuesSearchResult> {
        const { token, apiEndpoint } = await this.getAccessTokenAndApiEndpoint(endpoint);

        const queryParts: string[] = [];
        if (params?.field) queryParts.push(`field=${encodeURIComponent(params.field)}`);
        if (params?.q) queryParts.push(`q=${encodeURIComponent(params.q)}`);
        if (dataset) queryParts.push(`dataset=${dataset}`);
        const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

        const url = `${apiEndpoint}/UserDocument/schemas/field-values/search${queryString}`;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });
            const responseBody = await response.json();
            return responseBody;
        } catch (error) {
            console.error('Field values search error:', error);
            return [];
        }
    }

    /**
     * Private helper function to get the access token and API endpoint.
     */
    private async getAccessTokenAndApiEndpoint(endpoint?: string) {
        const token = await this.tokenService.getAccessToken([tokenScope]);

        if (!token) {
            throw new Error('Failed to acquire token');
        }
        let apiEndpoint = endpoint || this.trellisChatEndpoint

        if (apiEndpoint.endsWith('/')) {
            apiEndpoint = apiEndpoint.slice(0, -1);
        }

        return { token, apiEndpoint };
    }
}
